import { Flex } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useState, useEffect } from 'react';
import OrganizationSearchInput from './OrgonizationSearchInput';
import CustomCountrySelect from './CustomCountrySelect';
import CustomFormFieldTextInput from 'src/components/FormField/CustomFormFieldTextInput';
import { AffiliationCountryList } from 'src/api/queries';
import { IAffiliationDetails } from '@publishing-engine/domain/models';

type OrganizationDetailsFormProps = {
  formConfig: any;
  fieldPrefix: string;
  showSearch?: boolean;
  affiliationDetails?: IAffiliationDetails | null | undefined;
};

const OrganizationDetailsForm = ({
  formConfig,
  fieldPrefix,
  affiliationDetails
}: OrganizationDetailsFormProps) => {
  const [selectedCountry, setSelectedCountry] =
    useState<AffiliationCountryList | null>(null);

  const { setValue, reset, watch, clearErrors } = useFormContext();

  const selectedAffiliation = watch(`${fieldPrefix}.selectedAffiliation`);
  const ringgoldId = watch(`${fieldPrefix}.ringgoldId`);

  const organizationName = affiliationDetails?.organizationName;

  const resetData = () => {
    setValue(`${fieldPrefix}`, {
      organizationName: '',
      department: '',
      streetAddress: '',
      city: '',
      stateOrRegion: '',
      postalCode: '',
      country: '',
      countryCode: '',
      ringgoldId: '',
      url: '',
      selectedAffiliation: null,
      searchValue: '' // also clear the search value itself,
    });
    setSelectedCountry(null);
  };

  useEffect(() => {
    if (selectedAffiliation) {
      setValue(
        `${fieldPrefix}.organizationName`,
        selectedAffiliation?.name || ''
      );
      setValue(
        `${fieldPrefix}.streetAddress`,
        selectedAffiliation?.formattedLocation || ''
      );
      setValue(
        `${fieldPrefix}.department`,
        selectedAffiliation?.department || affiliationDetails?.department || ''
      );
      setValue(
        `${fieldPrefix}.postalCode`,
        selectedAffiliation?.postCode || ''
      );
      setValue(
        `${fieldPrefix}.stateOrRegion`,
        selectedAffiliation?.state || ''
      );
      setValue(`${fieldPrefix}.city`, selectedAffiliation?.city || '');
      setValue(`${fieldPrefix}.country`, selectedAffiliation?.country || '');
      setValue(
        `${fieldPrefix}.countryCode`,
        selectedAffiliation?.countryCode || ''
      );
      setValue(
        `${fieldPrefix}.ringgoldId`,
        selectedAffiliation?.ringgoldId ?? ''
      );
      setValue(`${fieldPrefix}.url`, selectedAffiliation?.primaryDomain ?? '');

      setSelectedCountry({
        country: selectedAffiliation?.country ?? '',
        countryCode: selectedAffiliation?.countryCode ?? ''
      });

      clearErrors(fieldPrefix);
    }
  }, [selectedAffiliation, reset]);

  useEffect(() => {
    if (affiliationDetails) {
      setSelectedCountry({
        country: affiliationDetails?.country ?? '',
        countryCode: affiliationDetails?.countryCode ?? ''
      });
    }
  }, [affiliationDetails]);

  useEffect(() => {
    const countryValue = watch(`${fieldPrefix}.country`);
    const countryCode = watch(`${fieldPrefix}.countryCode`);

    if (
      countryValue &&
      (!selectedCountry || selectedCountry.country !== countryValue)
    ) {
      setSelectedCountry({ country: countryValue, countryCode: countryCode });
    }
  }, [watch, fieldPrefix, selectedCountry]);

  return (
    <Flex direction={'column'} gap={3}>
      {formConfig.organizationName && (
        <Flex direction={'column'} gap={1}>
          <OrganizationSearchInput
            fieldPrefix={fieldPrefix}
            config={formConfig.organizationName}
            defaultValue={organizationName || ''}
            resetData={resetData}
            ringgoldId={ringgoldId}
          />
        </Flex>
      )}

      {formConfig.department && (
        <CustomFormFieldTextInput
          config={formConfig.department}
          fieldName={`${fieldPrefix}.department`}
        />
      )}

      {formConfig.streetAddress && (
        <CustomFormFieldTextInput
          config={formConfig.streetAddress}
          fieldName={`${fieldPrefix}.streetAddress`}
        />
      )}

      <Flex gap={4} flex={1} direction={{ base: 'column', md: 'row' }}>
        {formConfig.city && (
          <CustomFormFieldTextInput
            config={formConfig.city}
            fieldName={`${fieldPrefix}.city`}
          />
        )}
        {formConfig.stateOrRegion && (
          <CustomFormFieldTextInput
            config={formConfig.stateOrRegion}
            fieldName={`${fieldPrefix}.stateOrRegion`}
          />
        )}
        {formConfig.postalCode && (
          <CustomFormFieldTextInput
            config={formConfig.postalCode}
            fieldName={`${fieldPrefix}.postalCode`}
          />
        )}
      </Flex>

      {formConfig.country && (
        <CustomCountrySelect
          selectedCountry={selectedCountry}
          setSelectedCountry={countryObj => {
            setSelectedCountry(countryObj);
            setValue(`${fieldPrefix}.country`, countryObj?.country || '');
            setValue(
              `${fieldPrefix}.countryCode`,
              countryObj?.countryCode || ''
            );
          }}
          config={formConfig.country}
          fieldPrefix={fieldPrefix}
        />
      )}
      {formConfig.url && (
        <CustomFormFieldTextInput
          config={formConfig.url}
          fieldName={`${fieldPrefix}.url`}
        />
      )}
    </Flex>
  );
};

export default OrganizationDetailsForm;
